<div *ngIf="isLoading" class="meeting-loading">
  <i class="fa fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
</div>

<div *ngIf="!isLoading && meeting" class="meeting-detail-panel">
  <div class="meeting-header">
    <button class="btn btn-outline-secondary btn-sm back-btn" (click)="onBack()">
      <i class="fa fa-arrow-left" aria-hidden="true"></i>
      {{ 'BACK' | translate }}
    </button>
    <div class="meeting-header-main">
      <div class="title-row">
        <h4>{{ meeting.Title }}</h4>
        <span
          class="badge status-badge"
          [ngClass]="getStatusClass(meeting.Status)"
          [attr.title]="getMeetingStatusHelpKey(meeting.Status) | translate"
        >
          {{ getStatusLabel(meeting.Status) | translate }}
        </span>
        <span
          *ngIf="meeting.IsArchived"
          class="badge status-badge archived-status-badge"
          [attr.title]="'APPRAISAL_ARCHIVED_HINT' | translate"
        >
          {{ 'ARCHIVED' | translate }}
        </span>
        <span
          *ngIf="isHistoricalManagerViewer"
          class="badge status-badge readonly-status-badge"
          [attr.title]="'APPRAISAL_READ_ONLY_HISTORY_HINT' | translate"
        >
          {{ 'READ_ONLY' | translate }}
        </span>
        <span class="meeting-scheduled-date">
          {{ 'SCHEDULED_DATE' | translate }}:
          <strong>
            {{
              meeting.ScheduledDate
                ? (meeting.ScheduledDate | utcDate : 'dd-MM-yyyy HH:mm')
                : '—'
            }}
          </strong>
        </span>
      </div>
      <p *ngIf="!isEditing && meeting.Notes" class="meeting-description">{{ meeting.Notes }}</p>
    </div>
  </div>

  <div class="meeting-body">
    <div *ngIf="meeting.IsArchived" class="alert alert-info archived-meeting-banner">
      <i class="fa fa-archive" aria-hidden="true"></i>
      <span>
        {{ 'APPRAISAL_ARCHIVED_HINT' | translate }}
        <ng-container *ngIf="meeting.ArchivedOn">
          {{ 'ARCHIVED_ON' | translate }}:
          {{ meeting.ArchivedOn | utcDate : 'dd-MM-yyyy HH:mm' }}
        </ng-container>
      </span>
    </div>

    <div *ngIf="isHistoricalManagerViewer" class="alert alert-info readonly-meeting-banner">
      <i class="fa fa-eye" aria-hidden="true"></i>
      <span>{{ 'APPRAISAL_READ_ONLY_HISTORY_HINT' | translate }}</span>
    </div>

    <div *ngIf="isPendingAccessMeeting()" class="alert alert-warning pending-access-meeting-banner">
      <i class="fa fa-lock" aria-hidden="true"></i>
      <span>{{ getPendingAccessBannerKey() | translate }}</span>
    </div>

    <!-- Edit Form -->
    <div *ngIf="isEditing" class="meeting-edit-form">
      <div class="form-group">
        <label class="control-label">{{ 'TITLE' | translate }} <span class="required">*</span></label>
        <input
          type="text"
          class="form-control"
          [(ngModel)]="editTitle"
          maxlength="255" />
      </div>

      <div class="form-group">
        <label class="control-label">{{ 'EMPLOYEE' | translate }}</label>
        <select class="form-control" [(ngModel)]="editEmployeeUserId">
          <option *ngFor="let emp of employeeOptions" [ngValue]="emp.value">
            {{ emp.label }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="control-label">{{ 'SCHEDULED_DATE' | translate }}</label>
        <input
          type="datetime-local"
          class="form-control"
          [(ngModel)]="editScheduledDate"
          [min]="minScheduledDate" />
      </div>

      <div class="form-group">
        <label class="control-label">{{ 'END_TIME' | translate }}</label>
        <input
          type="datetime-local"
          class="form-control"
          [(ngModel)]="editEndTime"
          [min]="editScheduledDate || minScheduledDate" />
      </div>

      <div class="form-group">
        <label class="control-label">{{ 'APPRAISAL_MEETING_NOTES' | translate }}</label>
        <textarea
          class="form-control"
          rows="4"
          [(ngModel)]="editNotes">
        </textarea>
      </div>

      <div *ngIf="isAdmin || isManager" class="form-group">
        <label class="control-label">{{ 'APPRAISAL_MANAGER_PRIVATE_NOTES' | translate }}</label>
        <textarea
          class="form-control"
          rows="4"
          [(ngModel)]="editManagerPrivateNotes">
        </textarea>
      </div>

      <div class="form-actions edit-actions">
        <button
          class="btn btn-outline-secondary"
          [disabled]="isUpdating"
          (click)="onCancelEdit()">
          {{ 'CANCEL' | translate }}
        </button>
        <button
          class="btn btn-primary"
          [disabled]="isUpdating || !editTitle.trim()"
          (click)="onSaveEdit()">
          <i *ngIf="isUpdating" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          {{ 'SAVE' | translate }}
        </button>
      </div>
    </div>

    <!-- Read-only Meta Grid -->
    <div *ngIf="!isEditing" class="meta-grid">
      <div class="meta-card form-meta-card">
        <div class="meta-label">{{ 'FORM' | translate }}</div>
        <div class="meta-value form-meta-value">
          <span class="form-name" [attr.title]="meeting.TemplateName || null">{{ meeting.TemplateName || '—' }}</span>
          <div
            *ngIf="meeting.TemplateDescription"
            class="form-description"
            [attr.title]="meeting.TemplateDescription"
          >
            {{ meeting.TemplateDescription }}
          </div>
        </div>
      </div>
      <div class="meta-card person-meta-card">
        <div class="meta-label">{{ 'MANAGER' | translate }}</div>
        <div class="meta-value participant-meta-value">
          <span [attr.title]="getParticipantDisplayName(meeting.ManagerName, meeting.ManagerUserId)">
            {{ getParticipantDisplayName(meeting.ManagerName, meeting.ManagerUserId) }}
          </span>
          <span
            *ngIf="getParticipantStatusLabelKey(meeting.ManagerUserStatus) as managerStatusKey"
            class="badge participant-status-badge"
            [ngClass]="getParticipantStatusClass(meeting.ManagerUserStatus)"
          >
            {{ managerStatusKey | translate }}
          </span>
        </div>
      </div>
      <div class="meta-card person-meta-card">
        <div class="meta-label">{{ 'EMPLOYEE' | translate }}</div>
        <div class="meta-value participant-meta-value">
          <span [attr.title]="getParticipantDisplayName(meeting.EmployeeName, meeting.EmployeeUserId)">
            {{ getParticipantDisplayName(meeting.EmployeeName, meeting.EmployeeUserId) }}
          </span>
          <span
            *ngIf="getParticipantStatusLabelKey(meeting.EmployeeUserStatus) as employeeStatusKey"
            class="badge participant-status-badge"
            [ngClass]="getParticipantStatusClass(meeting.EmployeeUserStatus)"
          >
            {{ employeeStatusKey | translate }}
          </span>
        </div>
      </div>
      <div class="meta-card progress-meta-card" [attr.title]="getEmployeeProgressHintKey() | translate">
        <div class="meta-label">{{ 'APPRAISAL_PROGRESS' | translate }}</div>
        <div class="meta-value progress-meta">
          <div class="progress-summary">
            <span
              class="badge"
              [ngClass]="getEmployeeProgressClass()"
              [attr.title]="getEmployeeProgressHintKey() | translate"
            >
              {{ getEmployeeProgressLabelKey() | translate }}
            </span>
            <span *ngIf="meeting.EmployeeRequiredQuestionCount > 0" class="progress-ratio">
              {{ getRequiredProgressText() }}
            </span>
          </div>
          <small class="progress-caption">
            {{ getEmployeeProgressHintKey() | translate }}
          </small>
          <small
            *ngIf="meeting.Status !== AppraisalMeetingStatus.Completed && getEmployeeProgressTimestamp() as progressDate"
            class="progress-caption progress-caption-muted"
          >
            {{ getEmployeeProgressTimestampLabelKey() | translate }}:
            {{ progressDate | date : 'dd-MM-yyyy HH:mm' }}
          </small>
        </div>
      </div>
      <div *ngIf="meeting.ActionItemCount > 0" class="meta-card compact-meta-card">
        <div class="meta-label">{{ 'APPRAISAL_TASKS' | translate }}</div>
        <div class="meta-value">{{ meeting.ActionItemCount }}</div>
      </div>
      <div class="meta-card date-meta-card">
        <div class="meta-label">{{ 'CREATED_ON' | translate }}</div>
        <div class="meta-value">{{ toUtcTimestamp(meeting.CreatedOn) | date : 'dd-MM-yyyy HH:mm' }}</div>
      </div>
      <div class="meta-card date-meta-card">
        <div class="meta-label">{{ 'MODIFIED_ON' | translate }}</div>
        <div class="meta-value">{{
          meeting.ModifiedOn ? (toUtcTimestamp(meeting.ModifiedOn) | date : 'dd-MM-yyyy HH:mm') : '—'
        }}</div>
      </div>
    </div>

    <div
      *ngIf="!isEditing && (isAdmin || isManager) && meeting.ManagerPrivateNotes"
      class="well well-sm meeting-notes"
    >
      <strong>{{ 'APPRAISAL_MANAGER_PRIVATE_NOTES' | translate }}:</strong>
      <span>{{ meeting.ManagerPrivateNotes }}</span>
    </div>

    <div
      *ngIf="signatureBannerKey() as bannerKey"
      class="signature-banner"
      [ngClass]="'signature-banner-' + signatureBannerSeverity()"
    >
      <i
        class="fa"
        [ngClass]="{
          'fa-exclamation-triangle': signatureBannerSeverity() === 'warning',
          'fa-check-circle': signatureBannerSeverity() === 'success',
          'fa-info-circle': signatureBannerSeverity() === 'info'
        }"
        aria-hidden="true"
      ></i>
      <span>{{ bannerKey | translate }}</span>
    </div>

    <div
      *ngIf="requiresSignature() && !meeting.IsArchived"
      class="signature-status-panel"
    >
      <div class="signature-status-item">
        <i class="fa fa-user-circle" aria-hidden="true"></i>
        <span class="signature-status-label" translate>MANAGER</span>:
        <span *ngIf="meeting.ManagerSignedAt; else managerNotSigned">
          <i class="fa fa-check-circle text-success" aria-hidden="true"></i>
          {{ meeting.ManagerSignedName || meeting.ManagerName }}
          ({{ meeting.ManagerSignedAt | utcDate : 'dd-MM-yyyy HH:mm' }})
        </span>
        <ng-template #managerNotSigned>
          <span class="text-muted" translate>APPRAISAL_NOT_SIGNED</span>
        </ng-template>
      </div>
      <div class="signature-status-item">
        <i class="fa fa-user-circle-o" aria-hidden="true"></i>
        <span class="signature-status-label" translate>EMPLOYEE</span>:
        <span *ngIf="meeting.EmployeeSignedAt; else employeeNotSigned">
          <i class="fa fa-check-circle text-success" aria-hidden="true"></i>
          {{ meeting.EmployeeSignedName || meeting.EmployeeName }}
          ({{ meeting.EmployeeSignedAt | utcDate : 'dd-MM-yyyy HH:mm' }})
        </span>
        <ng-template #employeeNotSigned>
          <span class="text-muted" translate>APPRAISAL_NOT_SIGNED</span>
        </ng-template>
      </div>
    </div>

    <div *ngIf="(isAdmin || isManager) && !meeting.IsArchived" class="meeting-actions">
      <div class="btn-group status-group">
        <button
          *ngFor="let opt of statusOptions"
          class="btn btn-sm meeting-status-button"
          [ngClass]="getStatusButtonClass(opt.value)"
          [attr.title]="getStatusButtonTitleKey(opt.value) | translate"
          [disabled]="isStatusButtonDisabled(opt.value)"
          (click)="onStatusChange(opt.value)"
        >
          <i
            *ngIf="isUpdatingStatus && pendingStatusValue === opt.value"
            class="fa fa-spinner fa-spin"
            aria-hidden="true"></i>
          {{ opt.labelKey | translate }}
        </button>
      </div>
      <div class="meeting-actions-right">
        <button
          *ngIf="canSignMeeting()"
          class="btn btn-sm btn-primary"
          [disabled]="isMeetingActionPending()"
          (click)="onSign()">
          <i
            class="fa"
            [ngClass]="isSigning ? 'fa-spinner fa-spin' : 'fa-pencil-square-o'"
            aria-hidden="true"></i>
          {{ 'APPRAISAL_SIGN_AND_CONFIRM' | translate }}
        </button>
        <button
          *ngIf="!isEditing && !isMeetingLocked()"
          class="btn btn-sm btn-default"
          [disabled]="isMeetingActionPending()"
          (click)="onStartEdit()">
          <i class="fa fa-pencil" aria-hidden="true"></i>
          {{ 'EDIT' | translate }}
        </button>
        <button
          class="btn btn-sm btn-default"
          [disabled]="isMeetingActionPending() || isPendingAccessMeeting()"
          [attr.title]="
            isPendingAccessMeeting()
              ? ('APPRAISAL_MEETING_WAITING_FOR_ACCESS' | translate)
              : ('RESEND_EMAIL' | translate)
          "
          (click)="onResendEmail()">
          <i
            class="fa"
            [ngClass]="isResendingEmail ? 'fa-spinner fa-spin' : 'fa-envelope'"
            aria-hidden="true"></i>
          {{ 'RESEND_EMAIL' | translate }}
        </button>
        <button
          class="btn btn-sm btn-danger"
          [disabled]="isMeetingActionPending()"
          (click)="onDelete()">
          <i
            class="fa"
            [ngClass]="isDeletingMeeting ? 'fa-spinner fa-spin' : 'fa-trash'"
            aria-hidden="true"></i>
          {{ 'DELETE' | translate }}
        </button>
      </div>
    </div>

    <div
      *ngIf="isEmployee && !isAdmin && !isManager && canSignMeeting()"
      class="meeting-actions meeting-actions-employee"
    >
      <button
        class="btn btn-sm btn-primary"
        [disabled]="isMeetingActionPending()"
        (click)="onSign()">
        <i
          class="fa"
          [ngClass]="isSigning ? 'fa-spinner fa-spin' : 'fa-pencil-square-o'"
          aria-hidden="true"></i>
        {{ 'APPRAISAL_SIGN_AND_CONFIRM' | translate }}
      </button>
    </div>

    <ng-container *ngIf="getVisibleSections() as visibleSections">
      <div *ngIf="visibleSections.length > 0" class="sections-toolbar">
        <div class="sections-nav">
          <button
            *ngFor="let section of visibleSections; let sectionIndex = index"
            type="button"
            class="section-nav-chip"
            [attr.title]="getSectionStateHelpKey(section) | translate"
            [ngClass]="{
              active: isSectionExpanded(section.SectionId),
              'state-required-pending': getSectionState(section) === 'required-pending',
              'state-optional-left': getSectionState(section) === 'optional-left',
              'state-complete': getSectionState(section) === 'complete'
            }"
            (click)="scrollToSection(section.SectionId)"
          >
            <span class="chip-index">{{ sectionIndex + 1 }}</span>
            <span class="chip-title">{{ section.Title }}</span>
            <span
              *ngIf="isEmployee && getSectionState(section) !== 'neutral'"
              class="chip-state-icon"
              [attr.aria-label]="getSectionStateLabelKey(section) | translate"
            >
              <i class="fa" [ngClass]="getSectionStateIcon(section)" aria-hidden="true"></i>
            </span>
            <span class="chip-meta">{{ getSectionAnsweredCount(section) }}/{{ getVisibleQuestions(section).length }}</span>
          </button>
        </div>

        <button
          *ngIf="!isEmployee"
          type="button"
          class="btn btn-sm btn-default show-all-btn"
          [attr.aria-expanded]="areAllSectionsExpanded()"
          (click)="toggleAllSections()"
        >
          <i
            class="fa"
            [ngClass]="areAllSectionsExpanded() ? 'fa-compress' : 'fa-expand'"
            aria-hidden="true"
          ></i>
          {{ (areAllSectionsExpanded() ? 'COLLAPSE_ALL' : 'SHOW_ALL') | translate }}
        </button>
      </div>

      <div class="sections-stack">
      <section
        *ngFor="let section of visibleSections; let sectionIndex = index"
        class="section-card"
        [id]="getSectionAnchorId(section.SectionId)"
      >
        <ng-container *ngIf="getVisibleQuestions(section) as visibleQuestions">
        <div
          class="section-shell"
          [ngClass]="{
            'is-collapsed': !isSectionExpanded(section.SectionId),
            'state-required-pending': getSectionState(section) === 'required-pending',
            'state-optional-left': getSectionState(section) === 'optional-left',
            'state-complete': getSectionState(section) === 'complete'
          }"
        >
          <button
            type="button"
            class="section-header"
            [attr.aria-expanded]="isSectionExpanded(section.SectionId)"
            (click)="toggleSection(section.SectionId)"
          >
            <div class="section-header-main">
              <div class="section-index">{{ sectionIndex + 1 }}</div>
              <div class="section-heading">
                <h5>{{ section.Title }}</h5>
                <p *ngIf="section.Description">{{ section.Description }}</p>
              </div>
            </div>

            <div class="section-header-meta">
              <div
                *ngIf="isEmployee && getSectionState(section) !== 'neutral'"
                class="section-state-pill"
                [attr.title]="getSectionStateHelpKey(section) | translate"
                [ngClass]="{
                  'state-required-pending': getSectionState(section) === 'required-pending',
                  'state-optional-left': getSectionState(section) === 'optional-left',
                  'state-complete': getSectionState(section) === 'complete'
                }"
              >
                <i class="fa" [ngClass]="getSectionStateIcon(section)" aria-hidden="true"></i>
                <span>{{ getSectionStateLabelKey(section) | translate }}</span>
                <span *ngIf="getSectionStateCount(section) !== null" class="section-state-count">
                  {{ getSectionStateCount(section) }}
                </span>
              </div>
              <div class="section-progress">
                {{ getSectionAnsweredCount(section) }}/{{ visibleQuestions.length }}
              </div>
              <div class="section-stat">
                {{ visibleQuestions.length }} {{ 'QUESTIONS' | translate }}
              </div>
              <div
                *ngIf="hasSectionActionPoints(section)"
                class="section-stat section-action-point-stat"
              >
                {{ 'APPRAISAL_TASKS' | translate }} ({{ getSectionActionPointCount(section) }})
              </div>
              <i
                class="fa section-toggle-icon"
                [ngClass]="isSectionExpanded(section.SectionId) ? 'fa-chevron-up' : 'fa-chevron-down'"
                aria-hidden="true"
              ></i>
            </div>
          </button>

          <div *ngIf="isSectionExpanded(section.SectionId)" class="section-body">
            <div class="questions-list">
              <article
                *ngFor="
                  let question of visibleQuestions;
                  let questionIndex = index
                "
                class="question-card"
                [class.validation-error]="isQuestionMissingRequired(question)"
              >
                <div class="question-header">
                  <div class="question-header-main">
                    <div class="question-index">Q{{ questionIndex + 1 }}</div>
                    <div class="question-title">
                      <span class="question-title-text">{{ question.QuestionText }}</span>
                      <span
                        *ngIf="isQuestionRequiredForCurrentRespondent(question)"
                        class="badge question-required-badge"
                        [attr.title]="'THIS_FIELD_IS_REQUIRED' | translate"
                      >
                        {{ 'REQUIRED' | translate }}
                      </span>
                    </div>
                  </div>
                  <button
                    *ngIf="canCreateFollowUpTasks()"
                    type="button"
                    class="btn btn-outline-primary btn-sm question-task-btn action-point-create-btn"
                    (click)="openTaskDialog(question)"
                  >
                    <i class="fa fa-plus-circle" aria-hidden="true"></i>
                    {{ 'APPRAISAL_ADD_ACTION_POINT' | translate }}
                  </button>
                </div>

                <div *ngIf="isQuestionMissingRequired(question)" class="question-required-hint">
                  <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                  {{ 'THIS_FIELD_IS_REQUIRED' | translate }}
                </div>

                <div *ngIf="hasQuestionTasks(question.QuestionId)" class="question-task-chips">
                  <button
                    *ngFor="let task of getQuestionTasks(question.QuestionId)"
                    type="button"
                    class="question-task-chip"
                    [ngClass]="getTaskStatusClass(task.Status)"
                    [class.is-clickable]="canOpenTaskChip(task)"
                    [disabled]="!canOpenTaskChip(task)"
                    (click)="openTaskDialog(question, task)"
                  >
                    <span class="chip-title">{{ task.Title }}</span>
                    <span class="chip-meta">{{ task.AssignedToName || '—' }}</span>
                  </button>
                </div>

                <div class="question-answer-stack">
                  <ng-container *ngIf="getPrimaryResponseRole(question) !== null; else emptyResponse">
                    <ng-container
                      *ngTemplateOutlet="responseBlock; context: { role: getPrimaryResponseRole(question) }"
                    ></ng-container>

                    <ng-container *ngIf="getSecondaryResponseRole(question) !== null">
                      <ng-container
                        *ngTemplateOutlet="responseBlock; context: { role: getSecondaryResponseRole(question) }"
                      ></ng-container>
                    </ng-container>
                  </ng-container>

                  <ng-template #emptyResponse>
                    <div class="readonly-value empty-response">
                      —
                    </div>
                  </ng-template>

                  <ng-template #responseBlock let-role="role">
                    <div
                      class="question-answer"
                      [class.read-only]="!canEditResponseBlock(question, role)"
                      [class.secondary]="!isPrimaryDisplayedRole(question, role)"
                    >
                      <div class="answer-role-label">
                        {{ getResponseRoleLabel(role) | translate }}
                      </div>

                      <ng-container *ngIf="question.QuestionType === 1">
                        <textarea
                          *ngIf="canEditResponseBlock(question, role); else readonlyText"
                          class="form-control"
                          rows="3"
                          [(ngModel)]="responseDrafts[question.QuestionId].TextAnswer"
                          (blur)="onQuestionTouched(question.QuestionId)"
                        >
                        </textarea>
                        <ng-template #readonlyText>
                          <div class="readonly-value">
                            {{ getQuestionResponse(question, role)?.TextAnswer || '—' }}
                          </div>
                        </ng-template>
                      </ng-container>

                      <ng-container *ngIf="question.QuestionType === 2">
                        <div
                          *ngIf="canEditResponseBlock(question, role); else readonlyYesNo"
                          class="choice-row"
                        >
                          <label class="choice-option choice-yes"
                            [class.selected]="responseDrafts[question.QuestionId].YesNoAnswer === true">
                            <input type="radio"
                              [name]="'yesno_' + question.QuestionId"
                              [value]="true"
                              [(ngModel)]="responseDrafts[question.QuestionId].YesNoAnswer"
                              (change)="onQuestionTouched(question.QuestionId)" />
                            <span>{{ 'YES' | translate }}</span>
                          </label>
                          <label class="choice-option choice-no"
                            [class.selected]="responseDrafts[question.QuestionId].YesNoAnswer === false">
                            <input type="radio"
                              [name]="'yesno_' + question.QuestionId"
                              [value]="false"
                              [(ngModel)]="responseDrafts[question.QuestionId].YesNoAnswer"
                              (change)="onQuestionTouched(question.QuestionId)" />
                            <span>{{ 'NO' | translate }}</span>
                          </label>
                        </div>
                        <ng-template #readonlyYesNo>
                          <ng-container *ngIf="getQuestionResponse(question, role) as yesNoResponse; else emptyYesNoAnswer">
                            <div
                              *ngIf="
                                yesNoResponse.YesNoAnswer === true ||
                                yesNoResponse.YesNoAnswer === false;
                                else emptyYesNoAnswer
                              "
                              class="yesno-indicator"
                            >
                              <span class="yesno-pill yes-pill"
                                [class.active]="yesNoResponse.YesNoAnswer === true"
                                [class.inactive]="yesNoResponse.YesNoAnswer !== true">
                                <i class="fa fa-check" aria-hidden="true" *ngIf="yesNoResponse.YesNoAnswer === true"></i>
                                {{ 'YES' | translate }}
                              </span>
                              <span class="yesno-pill no-pill"
                                [class.active]="yesNoResponse.YesNoAnswer === false"
                                [class.inactive]="yesNoResponse.YesNoAnswer !== false">
                                <i class="fa fa-times" aria-hidden="true" *ngIf="yesNoResponse.YesNoAnswer === false"></i>
                                {{ 'NO' | translate }}
                              </span>
                            </div>
                          </ng-container>
                          <ng-template #emptyYesNoAnswer>
                            <div class="readonly-value empty-response">—</div>
                          </ng-template>
                        </ng-template>
                      </ng-container>

                      <ng-container *ngIf="question.QuestionType === 0">
                        <div
                          *ngIf="canEditResponseBlock(question, role); else readonlyLikert"
                          class="likert-row"
                        >
                          <span class="likert-boundary">{{
                            question.LikertLowLabel || ('DISAGREE' | translate)
                          }}</span>
                          <div class="likert-options">
                            <label *ngFor="let n of [1, 2, 3, 4, 5]"
                              class="likert-option"
                              [ngStyle]="likertToneStyles[n]"
                              [class.selected]="responseDrafts[question.QuestionId].LikertAnswer === n">
                              <input type="radio"
                                [name]="'likert_' + question.QuestionId"
                                [value]="n"
                                [(ngModel)]="responseDrafts[question.QuestionId].LikertAnswer"
                                (change)="onQuestionTouched(question.QuestionId)" />
                              <span>{{ n }}</span>
                            </label>
                          </div>
                          <span class="likert-boundary">{{
                            question.LikertHighLabel || ('AGREE' | translate)
                          }}</span>
                        </div>
                        <ng-template #readonlyLikert>
                          <div class="likert-readonly-row">
                            <span class="likert-label-low">{{
                              question.LikertLowLabel || ('DISAGREE' | translate)
                            }}</span>
                            <div class="likert-grades">
                              <span
                                *ngFor="let n of [1, 2, 3, 4, 5]"
                                class="likert-grade"
                                [ngStyle]="likertToneStyles[n]"
                                [ngClass]="getLikertGradeClass(n, getQuestionResponse(question, role)?.LikertAnswer)">
                                {{ n }}
                              </span>
                            </div>
                            <span class="likert-label-high">{{
                              question.LikertHighLabel || ('AGREE' | translate)
                            }}</span>
                          </div>
                        </ng-template>
                      </ng-container>
                    </div>
                  </ng-template>
                </div>
              </article>

              <div *ngIf="visibleQuestions.length === 0" class="empty-questions">
                {{ 'NO_QUESTIONS_IN_SECTION' | translate }}
              </div>
            </div>
          </div>
        </div>
        </ng-container>
      </section>
      </div>
    </ng-container>

    <div *ngIf="shouldShowFollowUpTasksPanel()" class="follow-up-tasks-panel">
      <div class="follow-up-header summary-header">
        <h5 translate>APPRAISAL_TASKS</h5>
        <button
          *ngIf="canCreateFollowUpTasks()"
          type="button"
          class="btn btn-outline-primary btn-sm action-point-create-btn"
          (click)="openTaskDialog()"
        >
          <i class="fa fa-plus-circle" aria-hidden="true"></i>
          {{ 'APPRAISAL_ADD_ACTION_POINT' | translate }}
        </button>
      </div>

      <div *ngIf="isLoadingTasks" class="tasks-loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
      </div>

      <div *ngIf="showTaskCreationInfo()" class="alert alert-info follow-up-info">
        {{ getTaskCreationInfoKey() | translate }}
      </div>

      <div *ngIf="!isLoadingTasks && meetingTasks.length > 0" class="table-responsive follow-up-table">
        <table class="table table-condensed table-hover">
          <thead>
            <tr>
              <th>{{ 'APPRAISAL_ACTION_POINT' | translate }}</th>
              <th>{{ 'DESCRIPTION' | translate }}</th>
              <th>{{ 'QUESTION' | translate }}</th>
              <th>{{ 'ASSIGN' | translate }}</th>
              <th>{{ 'STATUS' | translate }}</th>
              <th>{{ 'DUE_DATE' | translate }}</th>
              <th>{{ 'ACTIONS' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let task of meetingTasks">
              <td>
                <strong>{{ task.Title }}</strong>
              </td>
              <td>
                <div
                  class="task-description-preview"
                  [class.task-description-preview-truncated]="hasTruncatedTaskDescription(task.Description)"
                  [attr.title]="task.Description || null"
                >
                  {{ getTaskDescriptionPreview(task.Description) }}
                </div>
              </td>
              <td>{{ task.QuestionText || '—' }}</td>
              <td>{{ task.AssignedToName || '—' }}</td>
              <td>
                <span class="badge" [ngClass]="getTaskStatusClass(task.Status)">
                  {{ getTaskStatusLabel(task.Status) | translate }}
                </span>
              </td>
              <td>{{ task.DueDate ? (task.DueDate | utcDate : 'dd-MM-yyyy') : '—' }}</td>
              <td>
                <div class="task-action-buttons">
                  <button
                    *ngIf="canEditFollowUpTasks()"
                    class="btn btn-xs btn-default"
                    [disabled]="isTaskActionPending(task.Id)"
                    (click)="openTaskDialog(findQuestionById(task.QuestionId), task)"
                    [title]="'EDIT' | translate"
                  >
                    <i class="fa fa-pencil" aria-hidden="true"></i>
                  </button>
                  <button
                    *ngIf="canCompleteTask(task)"
                    class="btn btn-xs btn-success"
                    [disabled]="isTaskActionPending(task.Id)"
                    (click)="completeTask(task)"
                    [title]="'COMPLETE' | translate"
                  >
                    <i *ngIf="!isTaskCompleting(task.Id)" class="fa fa-check" aria-hidden="true"></i>
                    <i *ngIf="isTaskCompleting(task.Id)" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                  </button>
                  <button
                    *ngIf="canDeleteTask()"
                    class="btn btn-xs btn-danger"
                    [disabled]="isTaskActionPending(task.Id)"
                    (click)="deleteTask(task)"
                    [title]="'DELETE' | translate"
                  >
                    <i *ngIf="!isTaskDeleting(task.Id)" class="fa fa-trash" aria-hidden="true"></i>
                    <i *ngIf="isTaskDeleting(task.Id)" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div *ngIf="showEmployeeTaskAvailabilityInfo()" class="alert alert-info follow-up-info employee-task-info">
      <i class="fa fa-info-circle" aria-hidden="true"></i>
      {{ 'APPRAISAL_ACTION_POINTS_MANAGER_ADDS_AFTER_SUBMISSION' | translate }}
    </div>

    <div *ngIf="isManager || isEmployee" class="response-actions">
      <button
        *ngIf="canSaveManagerAnswers()"
        type="button"
        class="btn btn-success"
        [disabled]="isSaving"
        (click)="onSaveManagerAnswers()"
      >
        <i *ngIf="isSaving" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
        {{ 'APPRAISAL_SAVE_ANSWERS' | translate }}
      </button>
      <button
        *ngIf="canSaveAndCompleteMeeting()"
        type="button"
        class="btn btn-primary"
        [disabled]="isSaving || isMeetingActionPending()"
        (click)="onSaveAndCompleteMeeting()"
      >
        <i
          *ngIf="isSaving || (isUpdatingStatus && pendingStatusValue === AppraisalMeetingStatus.Completed)"
          class="fa fa-spinner fa-spin"
          aria-hidden="true"></i>
        {{ 'APPRAISAL_SAVE_AND_COMPLETE_MEETING' | translate }}
      </button>
      <button
        *ngIf="canSaveDraft()"
        type="button"
        class="btn btn-outline-secondary"
        [disabled]="isSaving"
        (click)="onSaveDraft()"
      >
        <i *ngIf="isSaving" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
        {{ getResponseSaveLabelKey() | translate }}
      </button>
      <button
        *ngIf="canSubmitEmployeeResponses()"
        class="btn btn-primary"
        [disabled]="isSaving || isMeetingLocked() || hasUnansweredRequired()"
        (click)="onSaveResponses()"
      >
        <i *ngIf="isSaving" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
        {{ 'SUBMIT_RESPONSES' | translate }}
      </button>
      <div *ngIf="canSubmitEmployeeResponses() && hasUnansweredRequired()" class="validation-hint">
        <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
        {{ getUnansweredRequiredCount() }} {{ 'REQUIRED_QUESTIONS_UNANSWERED' | translate }}
      </div>
    </div>

    <p-dialog
      [(visible)]="showTaskDialog"
      [modal]="true"
      [closable]="true"
      [dismissableMask]="true"
      [style]="{ width: '620px', maxWidth: '95vw' }"
      styleClass="appraisal-question-task-dialog"
      (onHide)="closeTaskDialog()"
      [header]="getTaskDialogHeaderKey() | translate"
    >
      <div class="task-dialog-content">
        <div class="detail-card" *ngIf="taskDialogQuestion">
          <span class="detail-label">{{ 'QUESTION' | translate }}</span>
          <span class="detail-value">{{ taskDialogQuestion.QuestionText }}</span>
        </div>

        <div class="row">
          <div class="col-md-6">
            <label class="control-label">{{ 'TITLE' | translate }} *</label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="newTask.Title"
              [placeholder]="'APPRAISAL_ACTION_POINT' | translate"
            />
          </div>
          <div class="col-md-6">
            <label class="control-label">{{ 'ASSIGN' | translate }}</label>
            <select class="form-control" [(ngModel)]="newTask.AssignedToUserId">
              <option *ngFor="let option of taskAssignees" [ngValue]="option.id">
                {{ option.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <label class="control-label">{{ 'DESCRIPTION' | translate }}</label>
            <textarea
              class="form-control"
              rows="3"
              [(ngModel)]="newTask.Description"
              [placeholder]="'DESCRIPTION' | translate"
            ></textarea>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <label class="control-label">{{ 'DUE_DATE' | translate }}</label>
            <input
              type="date"
              class="form-control"
              [min]="minTaskDueDate"
              [(ngModel)]="newTask.DueDate"
            />
          </div>
        </div>

        <div class="task-dialog-actions">
          <button
            type="button"
            class="btn btn-outline-secondary"
            [disabled]="isSubmittingTask"
            (click)="closeTaskDialog()">
            {{ 'CLOSE' | translate }}
          </button>
          <button
            type="button"
            class="btn btn-primary action-point-create-btn"
            [disabled]="isSubmittingTask || !newTask.Title?.trim()"
            (click)="submitTaskDialog()"
          >
            <i *ngIf="isSubmittingTask" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            {{ getTaskDialogSubmitKey() | translate }}
          </button>
        </div>
      </div>
    </p-dialog>
  </div>
</div>

<p-confirmDialog></p-confirmDialog>
